import {
    AlertListTypeEnum,
    AlertOriginTypesEnum,
    AlertStatusTypeEnum,
    AlertSubTypes,
    AlertTypesEnum,
    EntityTypes,
    NotePurposeTypesEnum,
    NoteStatusTypes,
    NoteTypes,
    UserRightsEnum,
} from '../../../utilities/Constants';
import {
    IAlertListModel,
    IAlertListWithCountModel,
    IAlertSearchModel,
    INoteCategoryTypeModel,
    INoteModel,
} from '../../../interfaces/INote';
import { ICaseModel, ICaseTitleModel } from '../../../interfaces/ICase';
import { Offcanvas, Stack } from 'react-bootstrap';
import { cloneDeep, get } from 'lodash';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { AlertListAppliedFilters } from './AlertListAppliedFilters';
import { AlertListFiltersProvider } from './AlertListFiltersContext';
import { AlertListHeader } from './AlertListHeader';
import { AlertListSearchFilters } from './AlertListSearchFilters';
import { AlertListTable } from './AlertListTable';
import { AlertListTooManyRecordsNotification } from './AlertListTooManyRecordsModal';
import Authorization from '../../../stores/Authorization';
import CaseEasyUpdate from '../../../components/case/easy-update/CaseEasyUpdate';
import { CaseTriggerDetailsModal } from './CaseTriggerDetailsModal';
import { DocumentTitle } from '../../../components/shared/DocumentTitle';
import { IUserModel } from '../../../interfaces/IUser';
import { IValidation } from '../../../interfaces/IError';
import { Loader } from '../../../components/shared/Loader';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { MainContentOverlayComponent } from '../../../components/shared/MainContentOverlay';
import { NoteEditor } from '../../../components/notes/note-editor/NoteEditor';
import NoteHelper from '../../../utilities/NoteHelper';
import { PageWithAffixedHeader } from '../../../shared/PageWithAffixedHeader';
import Sort from '../../../stores/Sort';
import ValidateUtils from '../../../shared/validations';
import { getDocument } from 'pdfjs-dist';
import { showErrorToast } from '../../../shared/ToastUtils';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useGetAlerts } from './useGetAlerts';
import { useGetFavorites } from '../../../shared/react-query-hooks/useGetFavorites';
import { useLoading } from '../../../contexts/LoadingContext';
import { useNoteCategoryTypes } from '../../../shared/react-query-hooks/useNoteCategoryTypes';
import { useZoneContext } from '../../../contexts/useZoneContext';

const PageSize = 100;

const getDefaultStatusOptions = (alertListType: AlertListTypeEnum) => {
    if (alertListType === AlertListTypeEnum.MyAlertList) {
        return [
            { status: AlertStatusTypeEnum.Pending, read: true },
            { status: AlertStatusTypeEnum.Pending, read: false },
            { status: AlertStatusTypeEnum.Closed, read: false },
        ];
    } else if (alertListType === AlertListTypeEnum.ZoneAlertList) {
        return [{ status: AlertStatusTypeEnum.Pending }];
    }

    return [];
};

export const getAuthorizedOriginTypes = (
    user: IUserModel,
    alertListType: AlertListTypeEnum,
    zoneGuid: string
) => {
    const allowedOriginTypes = [];

    if (Authorization.userHasRight(UserRightsEnum.ViewCaseNotes, user, zoneGuid)) {
        allowedOriginTypes.push(AlertOriginTypesEnum.CaseNote);
    }

    if (Authorization.userHasRight(UserRightsEnum.ViewZoneNotes, user, zoneGuid)) {
        allowedOriginTypes.push(AlertOriginTypesEnum.ZoneNote);
    }

    if (Authorization.userHasViewStrategyNotesRight(user, zoneGuid)) {
        allowedOriginTypes.push(AlertOriginTypesEnum.StrategyNote);
    }

    if (
        Authorization.userHasRight(UserRightsEnum.CaseTriggerRules, user, zoneGuid) &&
        alertListType !== AlertListTypeEnum.ZoneAlertList
    ) {
        allowedOriginTypes.push(AlertOriginTypesEnum.CaseTrigger);
    }

    if (Authorization.userHasRight(UserRightsEnum.ViewPackageSettlement, user, zoneGuid)) {
        allowedOriginTypes.push(AlertOriginTypesEnum.PackageSettlementNote);
    }

    if (
        Authorization.userHasRight(UserRightsEnum.Reports, user, zoneGuid) &&
        alertListType === AlertListTypeEnum.MyAlertList
    ) {
        allowedOriginTypes.push(AlertOriginTypesEnum.Report);
    }

    return allowedOriginTypes;
};

export function AlertList() {
    const { type } = useParams();
    const { user } = useAuthContext();
    const { key: locationKey } = useLocation();
    const loading = useLoading();
    const zoneContext = useZoneContext();
    const noteCategoryTypesLookup = useNoteCategoryTypes();

    const alertListType = parseInt(type ?? '0');

    const getDefaultFilters = () => ({
        alertListType,
        originTypes: getAuthorizedOriginTypes(user, alertListType, zoneContext.zoneGuid),
        queryModel: { pageSize: PageSize, pageNumber: 0, sortBy: 0, sortDirection: 0 },
        statuses: getDefaultStatusOptions(alertListType),
    });

    const [validation, setValidation] = useState<IValidation>({});
    const [easyUpdateAttemptedCaseList, setEasyUpdateAttemptedCaseList] = useState<string[]>([]);
    const [searchResults, setSearchResults] = useState<IAlertListWithCountModel>();
    const [searchModel, setSearchModel] = useState<IAlertSearchModel>(getDefaultFilters());
    const [noteState, setNoteState] = useState<{
        currentNote: INoteModel;
        forceNoteEditorInReadOnlyMode?: boolean;
        openNoteEditor?: boolean;
    }>();

    const [easyUpdateGuid, setEasyUpdateGuid] = useState<string | undefined>(undefined);
    const [toggleEasyUpdate, setToggleEasyUpdate] = useState<boolean>(false);
    const [defaultFavoriteLoaded, setDefaultFavoriteLoaded] = useState<boolean>(false);
    const [caseTriggerAlertGuid, setCaseTriggerAlertGuid] = useState<string | undefined>(undefined);
    const [showFilterPane, setShowFilterPane] = useState<boolean>(false);

    const defaultFavoritesLookup = useGetFavorites(alertListType, true);

    const alertsApi = useGetAlerts(searchModel, alertListType, defaultFavoriteLoaded);

    const handleEmptyGuid = (value: string | null | undefined): string | undefined => {
        const emptyGuid = '00000000-0000-0000-0000-000000000000';

        if (!value || value === emptyGuid) {
            return undefined;
        }
        return value;
    };

    const getNoteType = (originTypeId: number): number => {
        let noteTypeId;

        switch (originTypeId) {
            case AlertOriginTypesEnum.StrategyNote:
                noteTypeId = NoteTypes.StrategyNote;
                break;
            case AlertOriginTypesEnum.ZoneNote:
                noteTypeId = NoteTypes.ZoneNote;
                break;
            case AlertOriginTypesEnum.PackageSettlementNote:
                noteTypeId = NoteTypes.PackageSettlementNote;
                break;
            default:
                noteTypeId = NoteTypes.CaseNote;
                break;
        }

        return noteTypeId;
    };

    const getCaseTitle = () => {
        const caseTitle: ICaseTitleModel = {};
        if (noteState?.currentNote.guid) {
            const match = searchResults?.data.find(
                (x) => x.originEntitySubKey === noteState.currentNote.guid
            );
            if (match) {
                caseTitle.caseName = match.originEntityName;
                caseTitle.priority = match.priority;
                caseTitle.SQScore = match.sqScore;
                caseTitle.SQProjection = match.sqProjection;
                caseTitle.guid = match.originEntityKey;
            }
        }

        return caseTitle;
    };

    const getCaseLocalCounselOrgGuid = () => {
        if (noteState?.currentNote.guid) {
            const match = searchResults?.data.find(
                (x) => x.originEntitySubKey === noteState.currentNote.guid
            );
            if (match) {
                return handleEmptyGuid(match.localCounselOrganizationGuid);
            }
        }
    };

    const getValidNoteCategories = () => {
        const validNoteCategoryFilter = (category: INoteCategoryTypeModel) =>
            searchModel.originTypes?.map((opt) => getNoteType(opt)).includes(category.noteType.id);

        const noteCategoryTypes =
            noteCategoryTypesLookup.data?.filter(validNoteCategoryFilter) ?? [];
        return noteCategoryTypes.sort(Sort.compareValues('label'));
    };

    const handleMarkCaseTriggerAsRead = async (
        id: string,
        openCaseTriggerDetailPopup?: boolean
    ) => {
        const searchResultsDataCopy = cloneDeep(searchResults?.data ?? []);
        const match = searchResultsDataCopy.find((x) => x.guid === id);

        const alertType = AlertTypesEnum.CaseTrigger;
        if (match && !match?.alertReadForLoggedInUser) {
            loading.showLoading();

            // todo: move to react-query mutation
            const response = await zoneContext.zoneApiClient.markAlertAsRead(id, alertType);
            loading.hideLoading();

            if (openCaseTriggerDetailPopup) {
                setCaseTriggerAlertGuid(id);
            }

            if (response.httpResponse.status == 401) {
                window.location.reload();
                return;
            }

            if (!response.httpResponse.ok) {
                showErrorToast('Failed to mark the alert as read');
            }

            if (response.errorMessage) {
                setValidation(ValidateUtils.parseErrors(response.errors, response.errorMessage));
                return;
            }

            match.alertReadForLoggedInUser = true;
            match.status = 'Closed';
        } else {
            if (openCaseTriggerDetailPopup) {
                setCaseTriggerAlertGuid(id);
            }
        }

        setSearchResults((prev) => ({ ...prev, data: searchResultsDataCopy }));
    };

    const handleViewNoteClick = (item: IAlertListModel) => {
        setNoteState({
            currentNote: {
                alertReadForLoggedInUser: true,
                guid: item.originEntitySubKey,
                purpose: { id: 0 },
                status: { id: 0 },
                type: { id: getNoteType(item.originTypeId) },
            },
            forceNoteEditorInReadOnlyMode: true,
            openNoteEditor: true,
        });
    };

    const handleShowEasyUpdateChange = (checked: boolean) => {
        setToggleEasyUpdate(checked);
    };

    const handleFavoriteSelected = (settingJson?: string) => {
        if (settingJson) {
            new Promise((resolve) => {
                setSearchModel(JSON.parse(settingJson));
                resolve(null);
            }).then(() => {
                alertsApi.refetch();
            });
        }
    };

    const handleDefaultFavoriteChange = (settingJson?: string) => {
        if (settingJson) {
            const model = JSON.parse(settingJson);
            setSearchModel(model);
            defaultFavoritesLookup.refetch();
        }
    };

    const validateFilters = () => {
        if (
            alertListType === AlertListTypeEnum.ZoneAlertList &&
            (searchModel.alertTypeIds?.length ?? 0) > 0 &&
            (searchModel.alertUserGuids?.length ?? 0) === 0
        ) {
            setValidation((prev) => ({
                ...prev,
                model: ['Please select a User in order to search by Alert Type'],
            }));
            return false;
        } else {
            setValidation((prev) => ({
                ...prev,
                model: [],
            }));
        }

        const noOptionsSelected = [
            searchModel.originTypes,
            searchModel.alertTypeIds,
            searchModel.alertSubTypeIds,
            searchModel.alertUserGuids,
            searchModel.noteCategories,
            searchModel.noteSubCategories,
            searchModel.purpose,
            searchModel.statuses,
        ].every((arr) => (arr?.length ?? 0) === 0);

        if (noOptionsSelected) {
            setValidation((prev) => ({
                ...prev,
                model: ['Enter at least one filter criteria'],
            }));
            return false;
        } else {
            setValidation((prev) => ({
                ...prev,
                model: [],
            }));
        }

        if ((searchModel.originTypes?.length ?? 0) === 0) {
            setValidation((prev) => ({
                ...prev,
                model: ['Enter at least one Type to filter'],
            }));
            return false;
        } else {
            setValidation((prev) => ({
                ...prev,
                model: [],
            }));
        }

        return true;
    };

    const handleRefreshClick = () => {
        const valid = validateFilters();

        if (valid) {
            alertsApi.refetch();
        }
    };

    const handleValidateFavorites = () => {
        const valid = (searchModel.originTypes?.length ?? 0) > 0;

        if (valid) {
            setValidation({
                favorites: [],
            });
        } else {
            setValidation((prev) => ({
                ...prev,
                favorites: ['Enter at least one type to save a favorite'],
            }));
        }

        return valid;
    };

    const handleApplyFilters = (searchModel: IAlertSearchModel) => {
        new Promise((resolve) => {
            setSearchModel(searchModel);
            resolve(null);
        }).then(() => {
            alertsApi.refetch();
        });
    };

    const handleSortData = (key: string, order: string, subKey?: string, subGrandKey?: string) => {
        const searchResultsCopy = cloneDeep(searchResults);

        const purposeColumnSorter = (a: IAlertListModel, b: IAlertListModel) => {
            const isAsc = order === 'asc';
            const direction = isAsc ? 1 : -1;

            // Handle undefined values: place them at the end
            if (a.subTypeId === undefined && b.subTypeId === undefined) return 0;
            if (a.subTypeId === undefined) return 1;
            if (b.subTypeId === undefined) return -1;

            // Sort by subTypeId first
            if (a.subTypeId !== b.subTypeId) {
                return direction * (a.subTypeId - b.subTypeId);
            }

            const purposeA = a.purpose ?? '';
            const purposeB = b.purpose ?? '';

            // If subTypeId is the same, sort by purpose
            return direction * purposeA.localeCompare(purposeB);
        };

        if (searchResultsCopy) {
            if (key === 'purpose') {
                searchResultsCopy.data.sort(purposeColumnSorter);
            } else if (key.includes('date')) {
                searchResultsCopy.data.sort(Sort.compareDate(key, subKey, order));
            } else {
                searchResultsCopy.data.sort(Sort.compareValues(key, subKey, order, subGrandKey));
            }
        }

        setSearchResults(searchResultsCopy);
        return;
    };

    const handleEasyUpdateClick = (alertListItem: IAlertListModel) => {
        const caseGuid = alertListItem.originEntityKey!;

        const noteStateCopy = cloneDeep(noteState);

        if (noteStateCopy) {
            noteStateCopy.currentNote.type = { id: getNoteType(alertListItem.originTypeId) };
            setNoteState(noteStateCopy);
        }

        setEasyUpdateGuid(caseGuid);
        setEasyUpdateAttemptedCaseList((prev) => [...prev, caseGuid]);
    };

    const handleItemNameClick = (item: IAlertListModel) => {
        const listCopy = cloneDeep(searchResults?.data ?? []);

        listCopy
            .filter((i) => i.originEntityKey === item.originEntityKey)
            .filter((i) => i.subTypeId !== AlertSubTypes.Chat.Value)
            .forEach((i) => (i.alertReadForLoggedInUser = true));

        setSearchResults((prev) => ({ ...prev, data: listCopy }));
    };

    const handleEasyUpdateComplete = (updatedCase: ICaseModel) => {
        const listCopy = cloneDeep(searchResults?.data ?? []);
        const found = listCopy.find((item) => item.originEntityKey == updatedCase.guid);

        if (found) {
            found.originEntityName = updatedCase.caseName;
            found.sqScore = updatedCase.qScore;
        }

        setEasyUpdateGuid(undefined);
        setSearchResults((prev) => ({ ...prev, data: listCopy }));
    };

    const handleRowEditClick = (item: IAlertListModel): void => {
        setNoteState({
            currentNote: {
                alertReadForLoggedInUser: true,
                guid: item.originEntitySubKey,
                purpose: { id: 0 },
                status: { id: 0 },
                type: { id: getNoteType(item.originTypeId) },
            },
            forceNoteEditorInReadOnlyMode: false,
            openNoteEditor: true,
        });
    };

    const handleNoteEditorSave = (noteItem: INoteModel) => {
        const dataCopy = cloneDeep(searchResults?.data ?? []);

        const foundIdx = dataCopy.findIndex((listItem) => listItem.guid === noteItem.alert?.guid);

        if (foundIdx > -1) {
            const note = cloneDeep(noteItem) as INoteModel;
            const allowEdit = NoteHelper.allowNoteEdit(
                note,
                user,
                zoneContext,
                note.originStrategyType
            );

            const originType =
                noteItem.alert?.originType?.displayName ??
                noteItem.alert?.originType?.name ??
                dataCopy[foundIdx].originType ??
                '';

            const originTypeId = noteItem.alert?.originType?.id ?? dataCopy[foundIdx].originTypeId;

            //One note may have ACT and Watch Alert  Report / Trigger have collab act / chat act / review act.
            //after save note need to get from note alerts.. but get the right alert from note..
            const alertTypeId = dataCopy[foundIdx].alertTypeId;
            const status =
                noteItem.alert?.status?.displayName ??
                noteItem.alert?.status?.name ??
                dataCopy[foundIdx].status ??
                '';

            const newAlertObj = {
                actionTargetName: `${note.actionTarget?.profile?.firstName} ${note.actionTarget?.profile?.lastName}`,
                alertReadForLoggedInUser: note.alertReadForLoggedInUser,
                alertTypeId: alertTypeId, //Origintype cannot be alert type, currently this is not being used in UI to perform any operations.
                //Alert types are - Act, watch, CaseTrigger, Report.
                //Origin types are like wher this alert has been derived / originated like
                // CaseNote = 1,  ZoneNote = 2,  StrategyNote = 3,  PackageSettlementNote = 4, Report = 5, CaseTrigger = 6,
                allowEdit,
                decision: note.decisionText,
                details: note.categories?.map((cat) => cat.displayName).join('\n'),
                guid: noteItem.alert?.guid,
                isLoggedInUserWatchUser: note.watchUser,
                localCounselOrganizationGuid: note.localCounselOrganizationGuid,
                modifiedDate: note.createdDate,
                originEntityKey: note.originEntityKey,
                originEntityName: note.originEntityName,
                originEntitySubKey: note.guid,
                originSubEntityType: EntityTypes.Note,
                originTypeId: originTypeId,
                originType,
                purpose: note.purpose.displayName,
                sqScore: note.qScore,
                status,
                writer: `${note.author?.profile?.firstName} ${note.author?.profile?.lastName}`,
            } as IAlertListModel;

            dataCopy[foundIdx] = newAlertObj;
        }

        setNoteState({
            openNoteEditor: false,
            currentNote: {
                status: { id: NoteStatusTypes.Open },
                purpose: { id: NotePurposeTypesEnum.Info },
            },
        });

        setSearchResults((prev) => ({ ...prev, data: dataCopy }));
    };

    const handleNoteEditorCancel = (canceledAlertGuid: string) => {
        const dataCopy = cloneDeep(searchResults?.data ?? []);

        dataCopy
            .filter((i) => i.guid === canceledAlertGuid)
            .forEach((i) => (i.alertReadForLoggedInUser = true));

        setNoteState({
            currentNote: { status: { id: 0 }, purpose: { id: NotePurposeTypesEnum.Info } },
            openNoteEditor: false,
        });

        setSearchResults((prev) => ({ ...prev, data: dataCopy }));
    };

    useEffect(() => {
        setSearchResults(() => alertsApi.data);
    }, [alertsApi.data]);

    useEffect(() => {
        if (defaultFavoritesLookup.isSuccess && defaultFavoritesLookup.isFetched) {
            const defaultFav = defaultFavoritesLookup.data?.find((fav) => fav.default);

            if (defaultFav?.settingJson) {
                new Promise((resolve) => {
                    setSearchModel(JSON.parse(defaultFav.settingJson!));
                    resolve(true);
                }).then(() => {
                    setDefaultFavoriteLoaded(true);
                });
            } else {
                setDefaultFavoriteLoaded(true);
            }
        }
    }, [defaultFavoritesLookup.isSuccess, defaultFavoritesLookup.isFetched]);

    useEffect(() => {
        defaultFavoritesLookup.refetch();
    }, [locationKey]);

    useEffect(() => {
        if (!alertsApi.isFetching) {
            const maxRecordCount = searchResults?.maxLimit ?? 0;
            const isOverLimit = (searchResults?.count ?? 0) > maxRecordCount;

            if (isOverLimit) {
                showErrorToast(
                    <AlertListTooManyRecordsNotification maxRecordCount={maxRecordCount} />
                );
            }
        }
    }, [searchResults?.count, searchResults?.maxLimit, alertsApi.isFetching]);

    useEffect(() => {
        if (!alertsApi.isFetching) {
            setShowFilterPane(false);
        }
    }, [alertsApi.isFetching]);

    if (!defaultFavoriteLoaded) {
        return <Loader />;
    }

    const pageTitle =
        alertListType === AlertListTypeEnum.MyAlertList ? 'My Alert List' : 'Zone Alert List';

    const ESAD = noteState?.currentNote?.originStrategyType;

    // StrategicPlan = 1,
    // ExecutionPlan = 2,
    // PlaintiffsFirm = 3,
    // Jurisdiction = 4,

    // PackageSettlement = 7, // AR - TO DO - Remove; doesnt belong here

    // // The following are Strategy "Sub Types" because they have Strategy Notes but also its own Entity Type, Hence using the EntityTypeId for the Enum
    // Projects = 8,
    // LocalCounsel = 11,
    // Experts = 14 // AR - Thi
    const getDocumentTypeId = () => {
        return 999;
    };
    
    const getDocumentSubTypeId = () => {
        return 999;
    };
    
    const documentTypeId = getDocumentTypeId();

    const documentSubTypeId = getDocumentSubTypeId();
    return (
        <>
            <AlertListFiltersProvider alertListType={alertListType}>
                <DocumentTitle title={pageTitle} />
                {noteState?.openNoteEditor && (
                    <MainContentOverlayComponent>
                        <NoteEditor
                            caseLocalCounselGuid={getCaseLocalCounselOrgGuid()}
                            caseTitle={getCaseTitle()}
                            currentNote={cloneDeep(noteState.currentNote)}
                            defaultAlertTarget={
                                Authorization.isLocalScope(user)
                                    ? noteState.currentNote.caseManager
                                    : undefined
                            }
                            defaultWatchTargets={
                                Authorization.isLocalScope(user) &&
                                noteState.currentNote.alternateCaseManager
                                    ? [noteState.currentNote.alternateCaseManager]
                                    : undefined
                            }
                            isReadonlyMode={noteState.forceNoteEditorInReadOnlyMode}
                            onSaveComplete={handleNoteEditorSave}
                            onCancel={handleNoteEditorCancel}
                            noteCategoryTypes={getValidNoteCategories().filter(
                                (cat) => cat.noteType.id === noteState.currentNote.type?.id
                            )}
                            strategyTypeId={noteState.currentNote.originStrategyType}
                        />
                    </MainContentOverlayComponent>
                )}
                {Authorization.isAuthorizedToRoute(
                    LocalRoutes.CaseEasyUpdate,
                    user,
                    zoneContext.zoneGuid
                ) && (
                    <CaseEasyUpdate
                        caseGuid={easyUpdateGuid}
                        onComplete={handleEasyUpdateComplete}
                        onCancel={() => setEasyUpdateGuid(undefined)}
                        hidden={!easyUpdateGuid}
                        excludeNotes={true}
                        additionalHeaderText={
                            alertListType === AlertListTypeEnum.MyAlertList
                                ? 'Update Case Data | My Alert List'
                                : alertListType === AlertListTypeEnum.ZoneAlertList
                                  ? 'Update Case Data | Zone Alert List'
                                  : ''
                        }
                    />
                )}

                <CaseTriggerDetailsModal
                    currentAlert={searchResults?.data?.find(
                        (item) => item.guid === caseTriggerAlertGuid
                    )}
                    onClose={() => setCaseTriggerAlertGuid(undefined)}
                    open={!!caseTriggerAlertGuid}
                    user={user}
                />

                {!easyUpdateGuid && (
                    <>
                        <Offcanvas
                            show={showFilterPane}
                            onHide={() => setShowFilterPane(false)}
                            placement="end"
                            scroll
                            backdrop
                        >
                            <Offcanvas.Header
                                closeButton
                                style={{ borderBottom: '1px solid lightgray', height: '77px' }}
                            >
                                <Offcanvas.Title>
                                    <span style={{ fontSize: 20, fontWeight: 500 }}>
                                        {pageTitle} Filters
                                    </span>
                                </Offcanvas.Title>
                            </Offcanvas.Header>
                            <Offcanvas.Body>
                                <AlertListSearchFilters
                                    alertListType={alertListType}
                                    appliedSearchModel={searchModel}
                                    onApplyFilters={handleApplyFilters}
                                    isSearching={false}
                                />
                            </Offcanvas.Body>
                        </Offcanvas>
                        <PageWithAffixedHeader
                            printHide={noteState?.openNoteEditor}
                            headerComponent={
                                <AlertListFiltersProvider alertListType={alertListType}>
                                    <AlertListHeader
                                        alertListType={alertListType}
                                        currentSearchModel={searchModel}
                                        onDefaultFavoriteChange={handleDefaultFavoriteChange}
                                        onEasyUpdateChange={handleShowEasyUpdateChange}
                                        onFavoriteSelected={handleFavoriteSelected}
                                        onRefreshClick={handleRefreshClick}
                                        onValidateFavorites={handleValidateFavorites}
                                        pageTitle={pageTitle}
                                    />
                                    {validation.model && (
                                        <div className="row mb-2 text-danger">
                                            <div>{validation.model}</div>
                                        </div>
                                    )}
                                    {validation.favorites && (
                                        <div className="row mb-2 text-danger">
                                            <div>{validation.favorites}</div>
                                        </div>
                                    )}
                                    <AlertListAppliedFilters
                                        alertListType={alertListType}
                                        searchModel={searchModel}
                                        onApplyFilters={handleApplyFilters}
                                        onToggleFilterPane={() => setShowFilterPane(true)}
                                    />
                                </AlertListFiltersProvider>
                            }
                        >
                            {(alertsApi.isLoading || alertsApi.isFetching) && <Loader />}
                            {(searchResults?.count ?? 0) < (searchResults?.maxLimit ?? 0) && (
                                <AlertListTable
                                    alertListType={alertListType}
                                    allowEasyUpdate={toggleEasyUpdate}
                                    data={searchResults}
                                    onEasyUpdateClick={handleEasyUpdateClick}
                                    onEditClick={handleRowEditClick}
                                    onItemNameClick={handleItemNameClick}
                                    onMarkCaseTriggerAsReadClick={handleMarkCaseTriggerAsRead}
                                    onSortData={handleSortData}
                                    onViewNoteClick={handleViewNoteClick}
                                    previouslyViewedCases={easyUpdateAttemptedCaseList}
                                />
                            )}
                        </PageWithAffixedHeader>
                    </>
                )}
            </AlertListFiltersProvider>
        </>
    );
}
